'use client';

import React, { createContext, useCallback, useContext } from 'react';
import type { AcceptedCurrency, ExchangeRate } from '@dr-pam/common-types/database/client';

type ExchangeRateContextValue = {
	rates: ExchangeRate[];
	getRateForCurrency: (currency: AcceptedCurrency) => number;
	getConvertedPriceInCents: (priceInCents: number, currency: AcceptedCurrency) => number;
	convertPrice: (priceInCents: number, currency: AcceptedCurrency) => number;
};

const ExchangeRateContext = createContext<ExchangeRateContextValue | null>(null);

export function ExchangeRateProvider({
	children,
	initialRates,
}: {
	children: React.ReactNode;
	initialRates: ExchangeRate[];
}) {
	const getRateForCurrency = useCallback(
		(currency: AcceptedCurrency) => {
			if (currency === 'AUD') {
				return 1;
			}
			const exchangeRateObject = initialRates.find((r) => r.currency === currency);
			return Number(exchangeRateObject?.rate) || 1;
		},
		[initialRates],
	);

	const getConvertedPriceInCents = useCallback(
		(priceInCents: number, currency: AcceptedCurrency) => {
			if (currency === 'AUD') {
				return priceInCents;
			}
			const rate = getRateForCurrency(currency);
			return Math.round(priceInCents * rate);
		},
		[getRateForCurrency],
	);

	const convertPrice = useCallback(
		(priceInCents: number, currency: AcceptedCurrency) => {
			if (currency === 'AUD') {
				return priceInCents;
			}
			const rate = getRateForCurrency(currency);
			// round to nearest .99 for the currency
			return Math.round((priceInCents * rate) / 100) * 100 + 99;
		},
		[getRateForCurrency],
	);

	return (
		<ExchangeRateContext.Provider
			value={{ rates: initialRates, getRateForCurrency, getConvertedPriceInCents, convertPrice }}
		>
			{children}
		</ExchangeRateContext.Provider>
	);
}

export function useExchangeRates() {
	const context = useContext(ExchangeRateContext);
	if (!context) {
		throw new Error('useExchangeRates must be used within ExchangeRateProvider');
	}
	return context;
}
